import { Component, inject, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CustomersService } from '../../services/customers.service';
import { CustomerDetailSummary } from '../../customer.model';

/**
 * Read-only customer summary, used in two places on the customer detail view (pop-up and page):
 * the identity bar above the tabs (`[showStats]="false"`), which stays in view on every tab, and
 * the stat cards on the Dashboard tab (`[showIdentity]="false"`). Shows the customer's name + address (from the loaded customer record)
 * alongside last invoice, number outstanding, last payment (date/status/amount),
 * and credits on file (from the customer-summary endpoint).
 */
@Component({
  selector: 'app-customer-summary',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './customer-summary.component.html',
  styleUrls: ['./customer-summary.component.scss'],
  host: { class: 'component component-customer-summary' },
})
export class CustomerSummaryComponent implements OnInit, OnChanges {
  private customersService = inject(CustomersService);

  @Input() customerId!: string;
  @Input() customerData?: any;
  /** Whether to show the stat cards. When false the summary endpoint is not called. */
  @Input() showStats = true;
  /** Whether to show the name / account / address block. */
  @Input() showIdentity = true;

  summary: CustomerDetailSummary | null = null;
  isLoading = false;

  ngOnInit(): void {
    if (this.customerId && this.showStats) {
      this.loadSummary();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const idChanged = changes['customerId'] && !changes['customerId'].firstChange;
    const statsTurnedOn = changes['showStats'] && !changes['showStats'].firstChange && this.showStats;
    if ((idChanged || statsTurnedOn) && this.customerId && this.showStats) {
      this.loadSummary();
    }
  }

  loadSummary(): void {
    this.isLoading = true;
    this.customersService.getCustomerDetailSummary(this.customerId).subscribe({
      next: (summary) => {
        this.summary = summary;
        this.isLoading = false;
      },
      error: () => {
        this.summary = null;
        this.isLoading = false;
      },
    });
  }

  get customerName(): string {
    return (
      this.customerData?.displayName ||
      this.customerData?.companyName ||
      this.summary?.customerId ||
      '—'
    );
  }

  get addressLine(): string {
    const c = this.customerData;
    if (!c) return '';
    return [c.customerAddress].filter(Boolean).join(', ');
  }

  get cityStateZip(): string {
    const c = this.customerData;
    if (!c) return '';
    const cityState = [c.customerCity, c.customerState].filter(Boolean).join(', ');
    return [cityState, c.customerZip].filter(Boolean).join(' ');
  }

  get hasAddress(): boolean {
    return !!(this.addressLine || this.cityStateZip);
  }
}
